'use client';
import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

import { useBenzingaEdge } from '@benzinga/edge';
import { Table } from '@benzinga/table';

import { GovernmentTradeRow, SecurityRecentColumnsDef } from '../../../../../src/components/GovTrades';
import { fetchAllTickerTrades } from '../../trades-actions';
import { TradesToolbar } from './TradesToolbar';
import { ToolbarState, lockedTickerToolbar, presetToDateFrom } from './tradesQuery';

const OptimizelyBanner = dynamic(() => import('@benzinga/ads').then(module => module.OptimizelyBanner), { ssr: true });

// Rows per page on the profile Recent Trades tab — sized to fill the shared table height.
const ROWS_PER_PAGE = 14;

interface TickerTradesTableProps {
  ticker: string;
  /**
   * A small SSR preview of the ticker's trades (crawlable + all a free user ever
   * sees). Edge users lazy-load the full history client-side for filtering — so
   * the full dataset never ships in the initial page payload for free/anon users.
   */
  rows: GovernmentTradeRow[];
}

const parseDate = (value?: string) => {
  const time = value ? Date.parse(value) : NaN;
  return Number.isNaN(time) ? 0 : time;
};

/**
 * Recent Trades table for the security profile. Filters by member name / chamber
 * / date, sorts, and paginates client-side. Free/anon users get only the SSR
 * preview `rows`; Edge users lazy-fetch the full (cached) history on mount, so the
 * heavy dataset stays out of the initial payload for public visitors.
 */
export const TickerTradesTable = ({ ticker, rows }: TickerTradesTableProps) => {
  const { hasEdgeSubscription: hasPermission, isEdgeSubscriptionLoading } = useBenzingaEdge();

  const [toolbar, setToolbar] = useState<ToolbarState>(lockedTickerToolbar(ticker));
  const [memberFilter, setMemberFilter] = useState('');
  const [page, setPage] = useState(0);
  const [allRows, setAllRows] = useState<GovernmentTradeRow[]>(rows);
  const [loadedFull, setLoadedFull] = useState(false);

  const isPaywalled = !isEdgeSubscriptionLoading && !hasPermission;

  // Edge users pull the full history once (server action is cached), so filtering
  // spans everything; free/anon users keep the small preview only.
  useEffect(() => {
    if (isEdgeSubscriptionLoading || !hasPermission || loadedFull) return undefined;
    let active = true;
    fetchAllTickerTrades(ticker)
      .then(full => {
        if (active && Array.isArray(full) && full.length) {
          setAllRows(full as GovernmentTradeRow[]);
          setLoadedFull(true);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [hasPermission, isEdgeSubscriptionLoading, loadedFull, ticker]);

  const filteredSorted = useMemo(() => {
    const member = memberFilter.trim().toLowerCase();
    const chamber = toolbar.chamber.trim().toLowerCase();
    const dateFrom = presetToDateFrom(toolbar.datePreset);
    const dateFromTime = dateFrom ? parseDate(dateFrom) : null;

    const filtered = allRows.filter(row => {
      if (member && !(row.filer_info?.display_name ?? '').toLowerCase().includes(member)) return false;
      if (chamber && !(row.chamber || row.filer_info?.chamber || '').toLowerCase().includes(chamber)) return false;
      if (dateFromTime != null && parseDate(row.report_date) < dateFromTime) return false;
      return true;
    });

    const [field, direction] = (toolbar.sort || 'report_date:desc').split(':');
    const dir = direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = parseDate((a as GovernmentTradeRow)[field as 'report_date' | 'transaction_date']);
      const bv = parseDate((b as GovernmentTradeRow)[field as 'report_date' | 'transaction_date']);
      return (av - bv) * dir;
    });
  }, [allRows, memberFilter, toolbar]);

  // Free users see the most recent trades with filters locked behind Edge.
  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = hasPermission
    ? filteredSorted.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE)
    : filteredSorted.slice(0, ROWS_PER_PAGE);

  const resetPaging = () => setPage(0);

  const windowStart = Math.max(0, currentPage - 2);
  const windowEnd = Math.min(totalPages - 1, currentPage + 2);
  const pageWindow = Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index);

  return (
    <div className="all-trades">
      <TradesToolbar
        disabled={!hasPermission}
        lockedTicker={ticker}
        memberFilter={memberFilter}
        onChange={next => {
          setToolbar(next);
          resetPaging();
        }}
        onMemberFilterChange={value => {
          setMemberFilter(value);
          resetPaging();
        }}
        value={toolbar}
      />
      <div className={`gov-trades-table light ${isPaywalled ? 'show-login' : ''}`}>
        <Table
          columnsDef={SecurityRecentColumnsDef}
          height={789}
          noDataText="No trades match these filters."
          rowData={pageRows}
        />
        {isPaywalled && (
          <div className="all-trades-overlay">
            <OptimizelyBanner
              adType="gov-trades"
              buttonText="Sign up for Benzinga Edge"
              subtitle="Unlock full search, filters, and every congressional trade."
              title="Access with Benzinga Edge"
              utmSource="government-trades"
            />
          </div>
        )}
        {hasPermission && totalPages > 1 && (
          <div className="all-trades-pagination">
            <div className="pagination">
              {currentPage > 0 && (
                <button className="pg" onClick={() => setPage(currentPage - 1)}>
                  ‹
                </button>
              )}
              {pageWindow.map(pageNumber => (
                <button
                  className={`pg ${pageNumber === currentPage ? 'on' : ''}`}
                  key={pageNumber}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber + 1}
                </button>
              ))}
              {currentPage < totalPages - 1 && (
                <button className="pg" onClick={() => setPage(currentPage + 1)}>
                  ›
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
